import { useRef, useEffect, forwardRef } from 'react';
import { Input } from './ui/input';

/**
 * NumberInput — formatted numeric input with thousands separator (space).
 *
 * Contract:
 *   - value: number | '' (parent keeps clean numeric state — DB-ready)
 *   - onChange(next: number | '') — called on each valid edit
 *   - All other props forwarded to underlying <Input>
 *
 * Behaviour:
 *   - Displays "1 000 000" while accepting "1000000", "1 000 000", "1,000,000"
 *   - Strips non-digits and rejects decimals/negatives (integer-only per project spec)
 *   - Preserves cursor position while reformatting during typing
 *   - inputMode=numeric triggers numeric keyboard on mobile/iPad
 */

export const formatThousands = (n) => {
  if (n === '' || n === null || n === undefined) return '';
  const num = typeof n === 'number' ? n : parseInt(String(n).replace(/\D/g, ''), 10);
  if (!Number.isFinite(num)) return '';
  // Space thousands separator (non-breaking to prevent line-break inside number)
  return num.toLocaleString('fr-FR').replace(/\u202F|\u00A0/g, ' ');
};

// Strip all non-digit characters. Turns "1 000 000" / "1,000,000" / "1.000.000" / "abc123" → "1000000" / "123"
const toRawDigits = (s) => String(s ?? '').replace(/\D/g, '');

const NumberInputInner = ({ value, onChange, onBlur, ...rest }, ref) => {
  const inputRef = useRef(null);
  const setRefs = (el) => {
    inputRef.current = el;
    if (typeof ref === 'function') ref(el);
    else if (ref) ref.current = el;
  };

  // Keep cursor position stable when reformatting
  const caretRef = useRef(null);
  useEffect(() => {
    if (caretRef.current != null && inputRef.current && document.activeElement === inputRef.current) {
      try { inputRef.current.setSelectionRange(caretRef.current, caretRef.current); } catch { /* ignore */ }
      caretRef.current = null;
    }
  });

  const display = formatThousands(value);

  const handleChange = (e) => {
    const el = e.target;
    const rawBefore = el.value;
    const selStart = el.selectionStart ?? rawBefore.length;
    // How many digits are to the left of the caret in the CURRENT (pre-format) string
    const digitsLeftOfCaret = rawBefore.slice(0, selStart).replace(/\D/g, '').length;

    const digits = toRawDigits(rawBefore);
    const nextNum = digits === '' ? '' : parseInt(digits, 10);
    onChange(nextNum);

    // Predict the next formatted display so we can place the caret at the same digit-count position
    const nextDisplay = formatThousands(nextNum);
    // Walk nextDisplay and count digits; stop when we've passed `digitsLeftOfCaret` digits
    let digitsSeen = 0;
    let caretPos = nextDisplay.length;
    for (let i = 0; i < nextDisplay.length; i++) {
      if (/\d/.test(nextDisplay[i])) {
        if (digitsSeen === digitsLeftOfCaret) { caretPos = i; break; }
        digitsSeen++;
      }
    }
    if (digitsSeen === digitsLeftOfCaret) caretPos = Math.min(caretPos, nextDisplay.length);
    else caretPos = nextDisplay.length;
    caretRef.current = caretPos;
  };

  const handleBlur = (e) => {
    // Normalise any residual weirdness; display is already formatted via value.
    if (onBlur) onBlur(e);
  };

  const handlePaste = (e) => {
    // Paste already funnels through onChange via the native event, but we guard against odd separators.
    // No preventDefault needed — the subsequent onChange will strip non-digits.
  };

  return (
    <Input
      {...rest}
      ref={setRefs}
      type="text"
      inputMode="numeric"
      pattern="[0-9 ]*"
      autoComplete="off"
      value={display}
      onChange={handleChange}
      onBlur={handleBlur}
      onPaste={handlePaste}
    />
  );
};

export const NumberInput = forwardRef(NumberInputInner);
